import {
  type ChainFullId,
  ChainFullIds,
  type ChainId,
  ChainIds,
  type MultichainTokenBalance,
} from "@umami/chains";
import { type UnifiedBalanceWithPrices } from "@umami/state";
import { NATIVE_ETH_ADDRESS, ZEROX_ETH_ADDRESS } from "@umami/swap/src/services/swap";
import { formatEther } from "ethers";

export const TEZOS_TO_ETHERLINK_FEE = 0.001;
export const ETHERLINK_TO_TEZOS_FEE = 0.05;

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  errorType?:
    | "insufficient_balance"
    | "insufficient_native_fee"
    | "insufficient_multiple_native_fees"
    | "amount_exceeds_balance"
    | "fast_withdrawal_amount_error";
}

export interface BalanceValidationParams {
  sellToken: (UnifiedBalanceWithPrices & MultichainTokenBalance) | undefined;
  buyToken: (UnifiedBalanceWithPrices & MultichainTokenBalance) | undefined;
  sellAmount: string;
  feeEstimate: {
    nativeFee: bigint;
    zroFee: bigint;
    nativeFees?: Array<{
      amount: bigint;
      symbol: string;
      chainId?: string;
    }>;
  } | null;
  isGaslessSwap: boolean;
  nativeBalance: { balance: string };
  nativeSymbol: string;
  isFastWithdrawal?: boolean;
  fastWithdrawalMin?: number | null;
  fastWithdrawalMax?: number | null;
  getNetworkChain: (chainId: ChainFullId) => ChainId;
  getChainBalance: (chainId: ChainFullId) => { balance: string };
}

export class SwapValidator {
  /**
   * Check if sell amount exceeds the user's balance
   */
  static isSellAmountExceedingBalance(params: BalanceValidationParams): boolean {
    const {
      sellToken,
      buyToken,
      sellAmount,
      feeEstimate,
      isGaslessSwap,
      nativeSymbol,
      getNetworkChain,
    } = params;

    if (!sellToken || !buyToken || !sellAmount) {
      return false;
    }

    const sellAmountNum = parseFloat(sellAmount);
    const sellTokenBalanceNum = parseFloat(sellToken.balance);

    // Special case for Tezos-Etherlink swaps
    if (
      sellToken.symbol === "XTZ" &&
      this.isTezosEtherlinkSwap(sellToken, buyToken, getNetworkChain)
    ) {
      const feeAmount = this.getTezosEtherlinkFee(sellToken, buyToken, getNetworkChain);
      const totalRequired = sellAmountNum + feeAmount;

      return totalRequired > sellTokenBalanceNum;
    }

    // For native token swaps with network fees, include the fee
    if (
      !isGaslessSwap &&
      feeEstimate &&
      !this.isTezosEtherlinkSwap(sellToken, buyToken, getNetworkChain)
    ) {
      const isSellTokenNative = sellToken.symbol === nativeSymbol;

      if (isSellTokenNative) {
        const networkFee = parseFloat(formatEther(feeEstimate.nativeFee));
        const totalRequired = sellAmountNum + networkFee;

        return totalRequired > sellTokenBalanceNum;
      }
    }

    return sellAmountNum > sellTokenBalanceNum;
  }

  /**
   * Check if network fee exceeds native token balance (for non-native tokens)
   */
  static isNetworkFeeExceedingNativeBalance(params: BalanceValidationParams): boolean {
    const { sellToken, buyToken, feeEstimate, nativeBalance, getNetworkChain } = params;

    if (
      !sellToken ||
      !buyToken ||
      !feeEstimate ||
      this.isTezosEtherlinkSwap(sellToken, sellToken, getNetworkChain)
    ) {
      return false;
    }

    // If we have detailed native fees (from LiFi), check each one
    if (feeEstimate.nativeFees && feeEstimate.nativeFees.length > 0) {
      // Use the helper method to check if any fees are insufficient
      const insufficientFees = this.getInsufficientNativeFeesList(params);

      return insufficientFees.length > 0;
    }

    // For non-native tokens, check if we have enough native currency for the network fee
    const networkFee = parseFloat(formatEther(feeEstimate.nativeFee));
    const nativeBalanceNum = parseFloat(nativeBalance.balance);

    return networkFee > nativeBalanceNum;
  }

  /**
   * Get a list of insufficient native fees with detailed error messages
   */
  private static getInsufficientNativeFeesList(params: BalanceValidationParams): string[] {
    const { sellToken, feeEstimate, getNetworkChain, getChainBalance, nativeSymbol, sellAmount } =
      params;
    const insufficientFees: string[] = [];

    if (!sellToken || !feeEstimate?.nativeFees) {
      return insufficientFees;
    }

    const isSellTokenNative = sellToken.symbol === nativeSymbol;

    feeEstimate.nativeFees.forEach(fee => {
      const chainIdToCheck = fee.chainId ? (fee.chainId as ChainFullId) : sellToken.chainId;
      const chainBalance = getChainBalance(chainIdToCheck);
      const feeAmount = parseFloat(formatEther(fee.amount));
      const nativeBalanceNum = parseFloat(chainBalance.balance);

      // For native sell tokens on the same chain, include sell amount in calculation
      if (isSellTokenNative && chainIdToCheck === sellToken.chainId) {
        const sellAmountNum = parseFloat(sellAmount);
        const totalRequired = sellAmountNum + feeAmount;

        if (totalRequired > nativeBalanceNum) {
          insufficientFees.push(
            this.formatNativeFeeError(
              fee,
              sellAmountNum,
              feeAmount,
              getNetworkChain(chainIdToCheck)
            )
          );
        }
      }
      // For native buy tokens on the same chain, or non-native tokens/fees on different chains, just check the fee amount
      else if (feeAmount > nativeBalanceNum) {
        insufficientFees.push(
          this.formatFeeOnlyError(fee, feeAmount, getNetworkChain(chainIdToCheck))
        );
      }
    });

    return insufficientFees;
  }

  /**
   * Format error message for native token sales (sell amount + fee)
   */
  private static formatNativeFeeError(
    fee: { symbol: string },
    sellAmount: number,
    feeAmount: number,
    networkChain: string
  ): string {
    const totalRequired = sellAmount + feeAmount;

    return `${totalRequired.toFixed(6)} ${fee.symbol} (${sellAmount.toFixed(6)} + ${feeAmount.toFixed(6)} gas) on ${networkChain}`;
  }

  /**
   * Format error message for fee-only scenarios
   */
  private static formatFeeOnlyError(
    fee: { symbol: string },
    feeAmount: number,
    networkChain: string
  ): string {
    return `${feeAmount.toFixed(6)} ${fee.symbol} on ${networkChain}`;
  }

  /**
   * Create a specific error message for native token sales with insufficient balance
   */
  private static createNativeTokenSaleError(
    sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    sellAmount: string,
    feeAmount: number,
    nativeSymbol: string
  ): ValidationResult {
    return {
      isValid: false,
      error: `Sell amount (${sellAmount} ${sellToken.symbol}) + network fee (${feeAmount.toFixed(6)} ${nativeSymbol}) exceeds your balance (${sellToken.balance} ${sellToken.symbol})`,
      errorType: "insufficient_balance",
    };
  }

  /**
   * Create a generic network fee error message
   */
  private static createNetworkFeeError(
    networkFee: number,
    nativeSymbol: string,
    nativeBalance: string
  ): ValidationResult {
    return {
      isValid: false,
      error: `Network fee (${networkFee.toFixed(6)} ${nativeSymbol}) exceeds your ${nativeSymbol} balance (${nativeBalance} ${nativeSymbol})`,
      errorType: "insufficient_native_fee",
    };
  }

  /**
   * Check if multiple native fees exceed available balances
   */
  static getInsufficientMultipleNativeFeesError(
    params: BalanceValidationParams
  ): ValidationResult | null {
    const { sellToken, feeEstimate, getNetworkChain } = params;

    if (
      !sellToken ||
      !feeEstimate ||
      !feeEstimate.nativeFees ||
      feeEstimate.nativeFees.length === 0 ||
      this.isTezosEtherlinkSwap(sellToken, sellToken, getNetworkChain)
    ) {
      return null;
    }

    const insufficientFees = this.getInsufficientNativeFeesList(params);

    if (insufficientFees.length > 0) {
      return {
        isValid: false,
        error: `Insufficient native tokens for gas: ${insufficientFees.join(", ")}`,
        errorType: "insufficient_multiple_native_fees",
      };
    }

    return null;
  }

  /**
   * Check if sell amount only exceeds balance (without considering fees)
   */
  static isSellAmountOnlyExceedingBalance(params: BalanceValidationParams): boolean {
    const { sellToken, sellAmount, getNetworkChain } = params;

    if (!sellToken || !sellAmount) {
      return false;
    }

    const sellAmountNum = parseFloat(sellAmount);
    const sellTokenBalanceNum = parseFloat(sellToken.balance);

    // For Tezos-Etherlink swaps, this is handled in isSellAmountExceedingBalance
    if (
      sellToken.symbol === "XTZ" &&
      this.isTezosEtherlinkSwap(sellToken, sellToken, getNetworkChain)
    ) {
      return false;
    }

    return sellAmountNum > sellTokenBalanceNum;
  }

  /**
   * Check if fast withdrawal amount is within min/max constraints
   */
  static getFastWithdrawalAmountError(params: BalanceValidationParams): string | null {
    const { isFastWithdrawal, fastWithdrawalMin, fastWithdrawalMax, sellAmount } = params;

    if (
      isFastWithdrawal &&
      fastWithdrawalMin !== null &&
      fastWithdrawalMin !== undefined &&
      fastWithdrawalMax !== null &&
      fastWithdrawalMax !== undefined &&
      sellAmount &&
      !isNaN(Number(sellAmount))
    ) {
      const amt = Number(sellAmount);

      if (amt < fastWithdrawalMin) {
        return `Amount is below fast withdrawal minimum (${fastWithdrawalMin} XTZ)`;
      }

      if (amt > fastWithdrawalMax) {
        return `Amount exceeds fast withdrawal maximum (${fastWithdrawalMax} XTZ)`;
      }
    }

    return null;
  }

  /**
   * Get all validation errors for the current swap
   */
  static validateSwap(params: BalanceValidationParams): ValidationResult[] {
    const errors: ValidationResult[] = [];

    if (!params.sellToken || !params.buyToken || !params.sellAmount) {
      return errors;
    }

    // Check if sell amount exceeds balance
    if (this.isSellAmountExceedingBalance(params)) {
      const {
        sellToken,
        buyToken,
        sellAmount,
        feeEstimate,
        isGaslessSwap,
        nativeSymbol,
        getNetworkChain,
      } = params;
      const isSellTokenNative = sellToken.symbol === nativeSymbol;

      if (
        isSellTokenNative &&
        !isGaslessSwap &&
        feeEstimate &&
        !feeEstimate.nativeFees &&
        !this.isTezosEtherlinkSwap(sellToken, buyToken, getNetworkChain)
      ) {
        const networkFee = parseFloat(formatEther(feeEstimate.nativeFee));
        errors.push(
          this.createNativeTokenSaleError(sellToken, sellAmount, networkFee, nativeSymbol)
        );
      } else if (
        sellToken.symbol === "XTZ" &&
        this.isTezosEtherlinkSwap(sellToken, buyToken, getNetworkChain)
      ) {
        const feeAmount = this.getTezosEtherlinkFee(sellToken, buyToken, getNetworkChain);
        errors.push({
          isValid: false,
          error: `Sell amount (${sellAmount} ${sellToken.symbol}) + network fee (${feeAmount} ${sellToken.symbol}) exceeds your balance (${sellToken.balance} ${sellToken.symbol})`,
          errorType: "insufficient_balance",
        });
      } else {
        errors.push({
          isValid: false,
          error: `Sell amount (${sellAmount} ${sellToken.symbol}) exceeds your balance (${sellToken.balance} ${sellToken.symbol})`,
          errorType: "amount_exceeds_balance",
        });
      }
    }

    // Check if network fee exceeds native balance
    if (this.isNetworkFeeExceedingNativeBalance(params)) {
      const {
        feeEstimate,
        nativeBalance,
        nativeSymbol,
        sellToken,
        buyToken,
        isGaslessSwap,
        getNetworkChain,
      } = params;

      // If we have detailed native fees, use the helper method for consistent error messages
      if (feeEstimate?.nativeFees && feeEstimate.nativeFees.length > 0 && !isGaslessSwap) {
        const insufficientFees = this.getInsufficientNativeFeesList(params);

        if (insufficientFees.length > 0) {
          // For single fee issues, provide a more specific error message
          if (insufficientFees.length === 1) {
            const isSellTokenNative = sellToken.symbol === nativeSymbol;
            const isBuyTokenNative = buyToken.symbol === nativeSymbol;
            const sellTokenFee = feeEstimate.nativeFees.find(
              fee =>
                fee.chainId === sellToken.chainId || (!fee.chainId && fee.symbol === nativeSymbol)
            );
            const buyTokenFee = feeEstimate.nativeFees.find(
              fee => fee.chainId === buyToken.chainId
            );

            if (sellTokenFee && isSellTokenNative) {
              const feeAmount = parseFloat(formatEther(sellTokenFee.amount));

              errors.push(
                this.createNativeTokenSaleError(
                  sellToken,
                  params.sellAmount,
                  feeAmount,
                  nativeSymbol
                )
              );
            } else if (buyTokenFee && isBuyTokenNative) {
              const feeAmount = parseFloat(formatEther(buyTokenFee.amount));

              errors.push({
                isValid: false,
                error: `Insufficient ${buyToken.symbol} for network fee: ${feeAmount.toFixed(6)} ${buyToken.symbol} on ${getNetworkChain(buyToken.chainId)}`,
                errorType: "insufficient_native_fee",
              });
            } else {
              errors.push({
                isValid: false,
                error: `Insufficient native tokens for gas: ${insufficientFees[0]}`,
                errorType: "insufficient_native_fee",
              });
            }
          } else {
            // For multiple fee issues, use the consolidated error message
            errors.push({
              isValid: false,
              error: `Insufficient native tokens for gas: ${insufficientFees.join(", ")}`,
              errorType: "insufficient_multiple_native_fees",
            });
          }
        }
      } else {
        // For non-native tokens, use the original logic
        const networkFee = parseFloat(formatEther(feeEstimate!.nativeFee));
        errors.push(this.createNetworkFeeError(networkFee, nativeSymbol, nativeBalance.balance));
      }
    }

    const multipleNativeFeesError = this.getInsufficientMultipleNativeFeesError(params);

    if (multipleNativeFeesError) {
      errors.push(multipleNativeFeesError);
    }

    // Check fast withdrawal amount constraints
    const fastWithdrawalError = this.getFastWithdrawalAmountError(params);

    if (fastWithdrawalError) {
      errors.push({
        isValid: false,
        error: fastWithdrawalError,
        errorType: "fast_withdrawal_amount_error",
      });
    }

    return errors;
  }

  /**
   * Check if this is a Tezos-Etherlink swap
   */
  static isTezosEtherlinkSwap(
    sellToken: (UnifiedBalanceWithPrices & MultichainTokenBalance) | undefined,
    buyToken: (UnifiedBalanceWithPrices & MultichainTokenBalance) | undefined,
    getNetworkChain: (chainId: ChainFullId) => ChainId
  ): boolean {
    if (!sellToken || !buyToken) {
      return false;
    }

    return (
      (getNetworkChain(sellToken.chainId) === ChainIds.TEZOS &&
        getNetworkChain(buyToken.chainId) === ChainIds.ETHERLINK) ||
      (getNetworkChain(sellToken.chainId) === ChainIds.ETHERLINK &&
        getNetworkChain(buyToken.chainId) === ChainIds.TEZOS)
    );
  }

  /**
   * Get the fee for Tezos-Etherlink swaps
   */
  static getTezosEtherlinkFee(
    sellToken: (UnifiedBalanceWithPrices & MultichainTokenBalance) | undefined,
    buyToken: (UnifiedBalanceWithPrices & MultichainTokenBalance) | undefined,
    getNetworkChain: (chainId: ChainFullId) => ChainId
  ): number {
    if (!sellToken || !buyToken) {
      return 0;
    }

    if (
      getNetworkChain(sellToken.chainId) === ChainIds.TEZOS &&
      getNetworkChain(buyToken.chainId) === ChainIds.ETHERLINK
    ) {
      return TEZOS_TO_ETHERLINK_FEE;
    } else if (
      sellToken.chainId === ChainFullIds.ETHERLINK &&
      buyToken.chainId === ChainFullIds.TEZOS
    ) {
      return ETHERLINK_TO_TEZOS_FEE;
    }

    return 0;
  }

  /**
   * Check if this is a native token swap (ETH involved)
   */
  static isNativeTokenSwap(
    sellToken: (UnifiedBalanceWithPrices & MultichainTokenBalance) | undefined,
    buyToken: (UnifiedBalanceWithPrices & MultichainTokenBalance) | undefined
  ): boolean {
    if (!sellToken || !buyToken) {
      return false;
    }

    if (
      !(sellToken.chainId === ChainFullIds.ETHEREUM && buyToken.chainId === ChainFullIds.ETHEREUM)
    ) {
      return false;
    }

    const sellTokenAddress = sellToken.address;
    const buyTokenAddress = buyToken.address;

    return (
      sellTokenAddress === ZEROX_ETH_ADDRESS ||
      sellTokenAddress === NATIVE_ETH_ADDRESS ||
      buyTokenAddress === ZEROX_ETH_ADDRESS ||
      buyTokenAddress === NATIVE_ETH_ADDRESS
    );
  }

  /**
   * Check if we're bridging the same token between networks
   */
  static isBridgingSameTokenOnDifferentChains(
    sellToken: (UnifiedBalanceWithPrices & MultichainTokenBalance) | undefined,
    buyToken: (UnifiedBalanceWithPrices & MultichainTokenBalance) | undefined,
    getNetworkChain: (chainId: ChainFullId) => ChainId
  ): boolean {
    if (!sellToken || !buyToken) {
      return false;
    }

    return (
      sellToken.symbol === buyToken.symbol &&
      getNetworkChain(sellToken.chainId) !== getNetworkChain(buyToken.chainId)
    );
  }
}
